import { apiSlice } from "../services/apiSlice";

const QUERYFOLDERS = "/query-folders/";

// Interfaces for request parameters and bodies

export interface QueryFoldersParams {
  [key: string]: any;
}

export interface GetQueriesByFolderIdParams {
  id: string | number;
  format?: string;
  formatted?: boolean;
  page?: number;
  perPage?: number;
}

export interface GetColumnsByScopeParams {
  [key: string]: any;
}

export interface AddOrUpdateFolderBody {
  id?: string | number;
  name: string;
  parentId?: string | number | null;
  [key: string]: any;
}

export interface RunFolderBody {
  id?: string | number;
  page?: number;
  perPage?: number;
  query?: string;
}

export interface AddNewQueryBody {
  [key: string]: any;
}

// Interfaces for responses (replace `any` with actual types if known)
export interface QueryFolderTreeResponse {
  // define properties if known
  [key: string]: any;
}

export interface QueriesByFolderIdResponse {
  // define properties if known
  [key: string]: any;
}

export interface ColumnsByScopeResponse {
  // define properties if known
  [key: string]: any;
}

export interface CriteriaConditionsResponse {
  // define properties if known
  [key: string]: any;
}

export interface AddOrUpdateFolderResponse {
  // define properties if known
  [key: string]: any;
}

export interface AddNewQueryResponse {
  // define properties if known
  [key: string]: any;
}

export interface DeleteQueryFolderResponse {
  // define properties if known
  [key: string]: any;
}

export const searchQueryApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getQueryFolders: builder.query<QueryFolderTreeResponse, QueryFoldersParams>(
      {
        query: (params) => ({
          url: `${QUERYFOLDERS}tree`,
          params: params,
        }),
      }
    ),

    getAllQueries: builder.query({
      query: (params) => ({
        url: `/search-query`,
        params: params,
      }),
    }),

    getQueriesByFolderId: builder.mutation<
      QueriesByFolderIdResponse,
      GetQueriesByFolderIdParams
    >({
      query: ({ id, page, perPage }) => ({
        url: `${QUERYFOLDERS}${id}`,
      }),
    }),

    getQueryById: builder.mutation<
      QueriesByFolderIdResponse,
      GetQueriesByFolderIdParams
    >({
      query: ({ id }) => ({
        url: `/search-query/${id}`,
      }),
    }),

    getExportById: builder.mutation<
      QueriesByFolderIdResponse,
      GetQueriesByFolderIdParams
    >({
      query: (data) => {
        const { id, format, formatted } = data;

        const params = new URLSearchParams();
        if (format !== undefined) params.append("format", String(format));
        if (formatted !== undefined)
          params.append("formatted", String(formatted));
        return {
          url: `/search-query/export/${id}?${params}`,
          method: "GET",
        };
      },
    }),

    getColumnsByScope: builder.query<
      ColumnsByScopeResponse,
      GetColumnsByScopeParams
    >({
      query: (params) => ({
        url: `search-query/columns`,
        params,
      }),
    }),

    getColumnsByScopeAllPages: builder.query<any[], { scope: string; query?: string; }>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        let page = 1;
        const perPage = 50;
        let hasMore = true;
        let allResults: any[] = [];

        while (hasMore) {
          const result: any = await fetchWithBQ({
            url: "/search-query/columns",
            params: {
              scope: arg.scope,
              query: arg.query || "",
              page,
              perPage,
            },
          });

          if (result.error) return { error: result.error };

          const items = result.data?.items ?? [];
          allResults = [...allResults, ...items];

          hasMore = items.length === perPage;
          page++;
        }

        return { data: allResults };
      },
    }),

    getCriteriaConditions: builder.query<CriteriaConditionsResponse, void>({
      query: () => ({
        url: `search-query/operators`,
      }),
    }),

    addNewFolder: builder.mutation<
      AddOrUpdateFolderResponse,
      AddOrUpdateFolderBody
    >({
      query: (data) => ({
        url: `${QUERYFOLDERS}`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),

    addNewQuery: builder.mutation<AddNewQueryResponse, AddNewQueryBody>({
      query: (data) => ({
        url: `/search-query/live`,
        method: "POST",
        body: {
          ...data,
        },
      }),
    }),

    saveQuery: builder.mutation<AddNewQueryResponse, AddNewQueryBody>({
      query: (data) => ({
        url: data?.id ? `/search-query/${data.id}` : "/search-query",
        method: data?.id ? "PUT" : "POST",
        body: {
          ...data,
        },
      }),
    }),

    scheduledQuery: builder.mutation({
      query: (data) => ({
        url: `/search-query/${data.id}/schedule`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),

    updateFolder: builder.mutation<
      AddOrUpdateFolderResponse,
      AddOrUpdateFolderBody
    >({
      query: (data) => ({
        url: `${QUERYFOLDERS}${data?.id}`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),

    bulkNotes: builder.mutation({
      query: (data) => ({
        url: `notes/bulk`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),

    bulkRoles: builder.mutation({
      query: (data) => ({
        url: `contact-roles/bulk`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
    }),

    getContactRoles: builder.query({
      query: ({ page = 1, perPage = 50, query = '' }: { page?: number; perPage?: number; query?: string }) => ({
        url: `contact-roles?page=${page}&perPage=${perPage}&query=${query}`,
      }),
    }),
    runQuery: builder.mutation<AddOrUpdateFolderResponse, RunFolderBody>({
      query: (data) => {
        const { id, page, perPage, query } = data;

        const params = new URLSearchParams();
        if (page !== undefined) params.append("page", String(page));
        if (perPage !== undefined) params.append("perPage", String(perPage));
        if (query) params.append("query", query);

        return {
          url: `search-query/${id}/run?${params.toString()}`,
          method: "PUT",
        };
      },
    }),

    deleteQuery: builder.mutation<DeleteQueryFolderResponse, string | number>({
      query: (id) => ({
        url: `search-query/${id}`,
        method: "DELETE",
      }),
    }),

    bulkDelete: builder.mutation({
      query: ({ recordIds, scope }) => ({
        url: `search-query/bulk-delete?recordIds=${recordIds.join(
          ","
        )}&scope=${scope}`,
        method: "DELETE",
      }),
    }),

    deleteQueryFolder: builder.mutation<
      DeleteQueryFolderResponse,
      string | number
    >({
      query: (id) => ({
        url: `${QUERYFOLDERS}${id}`,
        method: "DELETE",
      }),
    }),
    getBulkUpdateFields: builder.query({
      query: (scope) => ({
        url: `search-query/bulk-fields?scope=${scope}`,
      }),
      providesTags: [{ type: 'fields', id: 'LIST' }],
    }),
    bulkUpdateFields: builder.mutation({
      query: (data) => ({
        url: `search-query/bulk-update`,
        method: "PUT",
        body: data
      }),
    }),
    getFile: builder.query({
      query: (fileKey) => ({
        url: `/search-query/get-file?s3Key=${fileKey}`,
        method: "GET",
      }),
    }),

    getScheduleQueryById: builder.mutation<
      QueriesByFolderIdResponse,
      GetQueriesByFolderIdParams
    >({
      query: ({ id }) => ({
        url: `/search-query/schedule/${id}`,
      }),
    }),

    cloneQuery: builder.mutation({
      query: (id) => ({
        url: `/search-query/clone/${id}`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetQueryFoldersQuery,
  useScheduledQueryMutation,
  useBulkNotesMutation,
  useBulkRolesMutation,
  useGetCriteriaConditionsQuery,
  useGetColumnsByScopeQuery,
  useGetColumnsByScopeAllPagesQuery,
  useGetQueriesByFolderIdMutation,
  useUpdateFolderMutation,
  useGetContactRolesQuery,
  useAddNewFolderMutation,
  useDeleteQueryFolderMutation,
  useGetAllQueriesQuery,
  useAddNewQueryMutation,
  useSaveQueryMutation,
  useRunQueryMutation,
  useGetExportByIdMutation,
  useGetQueryByIdMutation,
  useBulkDeleteMutation,
  useDeleteQueryMutation,
  useGetBulkUpdateFieldsQuery,
  useBulkUpdateFieldsMutation,
  useLazyGetFileQuery,
  useLazyGetContactRolesQuery,
  useCloneQueryMutation,
  useGetScheduleQueryByIdMutation,

} = searchQueryApiSlice;
